import { TreeFilterSelection } from '#domain/core/filterDefinition/treeFilterSelection';
import { FilterDefinitionFormType, type FilterDefinition, type FilterDefinitionValue } from '@carrefour-gcs/bff-sdk';

/** Form types holding a single value picked among the value options. */
const SINGLE_VALUE_FORM_TYPES: FilterDefinitionFormType[] = [
  FilterDefinitionFormType.RADIO,
  FilterDefinitionFormType.SELECT
];

/** Form types holding a list of values picked among the value options. */
const MULTI_VALUE_FORM_TYPES: FilterDefinitionFormType[] = [
  FilterDefinitionFormType.CHECKBOX,
  FilterDefinitionFormType.CHECKBOX_GRID,
  FilterDefinitionFormType.CHECKBOX_CARD,
  FilterDefinitionFormType.CHECKBOX_GROUP
];

/**
 * Return the filter definition value with correct label
 */
export function getFieldDefinitionValueOptions(filterDefinition: FilterDefinition): FilterDefinitionValue[] {
  if (!filterDefinition.valueOptions) {
    return [];
  }

  return filterDefinition.valueOptions.map((opt) => {
    const base = {
      value: opt.value,
      type: opt.type,
      label: filterDefinition.toTranslate ? `filters.field.value.${opt.label}` : opt.label,
      ...(opt.description !== undefined && { description: opt.description }),
      ...(opt.restrictions?.length && { restrictions: opt.restrictions })
    };

    if (opt.children && opt.children.length > 0) {
      return {
        ...base,
        children: getFieldDefinitionValueOptions({ ...filterDefinition, valueOptions: opt.children })
      };
    }

    return base;
  });
}

/**
 * Collects every selectable value of a filter definition, children included.
 */
function collectSelectableValues(valueOptions: FilterDefinitionValue[]): Set<string> {
  return valueOptions.reduce((selectableValues, option) => {
    selectableValues.add(option.value);

    if (option.children?.length) {
      collectSelectableValues(option.children).forEach((childValue) => selectableValues.add(childValue));
    }

    return selectableValues;
  }, new Set<string>());
}

/**
 * A value applied in a previous session may not be selectable anymore, typically when the
 * permission driving it has been revoked. Keeping it would leave the form with a value matching no option:
 * nothing appears selected while the validation keeps rejecting the invisible value. Such a value is dropped
 * so the user is simply asked to pick a new one.
 */
export function sanitizeValueAgainstOptions(filterDefinition: FilterDefinition, value: unknown): unknown {
  const valueOptions = filterDefinition.valueOptions;

  // Filters without options (DATE, TEXT...) hold a free value, and an empty option list means the definition
  // has not been enriched yet: in both cases there is nothing to check the value against.
  // A paginated filter only holds its first page of options: values selected beyond it are still valid.
  if (!valueOptions?.length || filterDefinition.isPaginated || value === null || value === undefined) {
    return value;
  }

  const selectableValues = collectSelectableValues(valueOptions);

  if (SINGLE_VALUE_FORM_TYPES.includes(filterDefinition.type)) {
    return selectableValues.has(value as string) ? value : undefined;
  }

  if (MULTI_VALUE_FORM_TYPES.includes(filterDefinition.type) && Array.isArray(value)) {
    return value.filter((item) => selectableValues.has(String(item)));
  }

  if (filterDefinition.type === FilterDefinitionFormType.CHECKBOX_TREE) {
    return new TreeFilterSelection(valueOptions).sanitize(value);
  }

  return value;
}
